import type { ReactNode } from "react";
import { Code, Layers, Settings2 } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { StepCodeBlock } from "../StepCodeBlock";
import { getString, getNumber } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getAggregateSummary(
  config: Record<string, unknown>,
  t: SummaryTranslator,
): ReactNode[] {
  const items: ReactNode[] = [];

  const code = getString(config.code);
  const maxRows = getNumber(config.maxRows) ?? getNumber(config.max_rows) ?? 100000;
  const outputAs = getString(config.outputAs) ?? getString(config.output_as) ?? "single";

  const outputLabels: Record<string, { labelKey: string; color: string }> = {
    single: {
      labelKey: "summaries.aggregate.output_single",
      color: "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300",
    },
    multiple: {
      labelKey: "summaries.aggregate.output_multiple",
      color: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
    },
  };
  const outputInfo = outputLabels[outputAs] || outputLabels.single;

  items.push(
    <StepInfoRow
      key="output"
      icon={<Layers className="size-3.5" />}
      label={t("summaries.aggregate.label_output")}
      value={
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${outputInfo.color}`}>
          {t(outputInfo.labelKey)}
        </span>
      }
    />,
  );

  items.push(
    <StepInfoRow
      key="maxRows"
      icon={<Settings2 className="size-3.5" />}
      label={t("summaries.aggregate.label_max_rows")}
      value={maxRows.toLocaleString()}
    />,
  );

  if (code) {
    items.push(
      <StepCodeBlock
        key="code"
        icon={<Code className="size-3.5" />}
        label={t("summaries.aggregate.label_js_code")}
        code={code}
        title="JavaScript Code"
      />,
    );
  }

  return items;
}
